import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONTENT_API = `${API_URL}/api/editable-content`;

/**
 * Hook to fetch editable content by key
 * Returns { content, loading, error, refetch }
 */
export function useEditableContent(key) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = useCallback(async () => {
    if (!key) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${CONTENT_API}/public/${key}`);
      setContent(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setContent(null); // Content not found - use default
      } else {
        setError(err.response?.data?.detail || 'Failed to load content');
      }
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return { content, loading, error, refetch: fetchContent };
}

/**
 * Hook to fetch multiple content blocks by keys
 * Returns { contents, loading, error, refetch }
 */
export function useEditableContents(keys) {
  const [contents, setContents] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContents = useCallback(async () => {
    if (!keys || keys.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${CONTENT_API}/public/bulk?keys=${keys.join(',')}`
      );
      setContents(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [keys]);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  return { contents, loading, error, refetch: fetchContents };
}

/**
 * Hook to fetch content by category
 */
export function useEditableContentCategory(category) {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContents = useCallback(async () => {
    if (!category) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${CONTENT_API}/public/category/${category}`
      );
      setContents(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  return { contents, loading, error, refetch: fetchContents };
}

/**
 * Admin hook for full CRUD operations on content
 */
export function useContentAdmin(token) {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async (category = null) => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const params = category ? `?category=${category}` : '';
      const response = await axios.get(`${CONTENT_API}/${params}`, { headers });
      setContents(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const create = async (data) => {
    try {
      const response = await axios.post(`${CONTENT_API}/`, data, { headers });
      setContents(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to create content');
    }
  };

  const update = async (id, data, changeNote = null) => {
    try {
      const params = changeNote ? `?change_note=${encodeURIComponent(changeNote)}` : '';
      const response = await axios.put(`${CONTENT_API}/${id}${params}`, data, { headers });
      setContents(prev => prev.map(c => c.id === id ? response.data : c));
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to update content');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${CONTENT_API}/${id}`, { headers });
      setContents(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to delete content');
    }
  };

  const getVersions = async (id) => {
    try {
      const response = await axios.get(`${CONTENT_API}/${id}/versions`, { headers });
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to load versions');
    }
  };

  const restoreVersion = async (contentId, versionId) => {
    try {
      await axios.post(`${CONTENT_API}/${contentId}/restore/${versionId}`, {}, { headers });
      await fetchAll();
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to restore version');
    }
  };

  const seedDefaults = async () => {
    try {
      const response = await axios.post(`${CONTENT_API}/seed-defaults`, {}, { headers });
      await fetchAll();
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to seed defaults');
    }
  };

  return {
    contents,
    loading,
    error,
    fetchAll,
    create,
    update,
    remove,
    getVersions,
    restoreVersion,
    seedDefaults
  };
}

export default useEditableContent;
